/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { simpleFetch } from "../../../restWrapper";

const DEBOUNCE_MS = 300;

function donorLabel(d, unnamedFallback) {
  return d.donorEmail || d.phoneNumber || unnamedFallback || "(unnamed donor)";
}

function DonorSearchPicker({ selected, onChange, disabled, languageObj }) {
  const lang = languageObj || {};
  const placeholder = lang.donorPlaceholder || "Search by email address";
  const searchingText = lang.donorSearching || "Searching…";
  const noResultsTpl = lang.donorNoResults || 'No donors found for "{q}"';
  const lockTooltip = lang.donorLockTooltip || "Upgrade to Premium to filter by donor";
  const unnamed = lang.donorUnnamed || "(unnamed donor)";
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      simpleFetch("/organizations/searchDonors", "GET", { q: query.trim() })
        .then((res) => {
          setLoading(false);
          if (res && res.status === 200 && res.body && Array.isArray(res.body.donors)) {
            setResults(res.body.donors);
          } else {
            setResults([]);
          }
        })
        .catch(() => {
          setLoading(false);
          setResults([]);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isSelected = (key) => selected.some((d) => d.key === key);

  const toggle = (donor) => {
    if (isSelected(donor.key)) {
      onChange(selected.filter((d) => d.key !== donor.key));
    } else {
      onChange([...selected, { key: donor.key, label: donorLabel(donor, unnamed) }]);
    }
  };

  const removeChip = (key) => {
    onChange(selected.filter((d) => d.key !== key));
  };

  return (
    <div
      className="DonorSearchPicker"
      ref={containerRef}
      title={disabled ? lockTooltip : undefined}
    >
      {selected.length > 0 && (
        <div className="DonorSearchPicker-chips">
          {selected.map((d) => (
            <span key={d.key} className="DonorSearchPicker-chip">
              <span className="DonorSearchPicker-chipLabel">{d.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className="DonorSearchPicker-chipRemove"
                  onClick={() => removeChip(d.key)}
                  aria-label="Remove"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="DonorSearchPicker-input"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && !disabled && query.trim() && (
        <div className="DonorSearchPicker-dropdown">
          {loading && (
            <div className="DonorSearchPicker-empty">{searchingText}</div>
          )}
          {!loading && results.length === 0 && (
            <div className="DonorSearchPicker-empty">
              {noResultsTpl.replace("{q}", query.trim())}
            </div>
          )}
          {!loading && results.map((donor) => (
            <label key={donor.key} className="DonorSearchPicker-row">
              <input
                type="checkbox"
                checked={isSelected(donor.key)}
                onChange={() => toggle(donor)}
              />
              <span className="DonorSearchPicker-rowLabel">
                {donorLabel(donor, unnamed)}
              </span>
              <span className="DonorSearchPicker-rowCount">
                {donor.paymentCount}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

DonorSearchPicker.propTypes = {
  selected: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  languageObj: PropTypes.object,
};

export default DonorSearchPicker;
